import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import { parseAPIError, APIErrorResponse } from '../utils/errors'
import type {
  UserDetail,
  UserProfileUpdate,
  OnboardingRequest,
  OnboardingWriterRequest,
  OnboardingStatusResponse,
  Writer,
  WriterCreate,
  WriterUpdate,
  ExternalResource,
  ExternalResourceCreate,
  ResourceType,
  ArticleSummary,
  ArticleDetail,
  ArticleCreate,
  ArticleUpdate,
  ArticleStatus,
  ArticlePreview,
  ArticlePreviewMarkdown,
  SchemaOrgSchemaResponse,
  GenerationTaskResponse,
  ConversationMessage,
  ConversationMessageCreate,
  ConversationResponse,
  ResearchedPost,
  ResearchedPostCreate,
  ResearchedPostUpdate,
  ResearchedPostListResponse,
  ContentIdea,
  ContentIdeaCreate,
  ContentIdeaUpdate,
  ContentIdeaListResponse,
  SemrushImportResponse,
  SemrushImportJobStatus,
  ParsedBusiness,
  ParsedWriter,
  AnalyzedBusiness,
  AnalyzedWriter,
  ResearchPostsRequest,
  PlanBlockResponse,
  AddCitationRequest,
  AddImageRequest,
  AddVideoRequest,
  ArticleContentSectionResponse,
  ContentSectionUpdateRequest,
  ContentSectionRegenerateRequest,
  LinkedInIntegration,
  LinkedInOAuthUrl,
  LinkedInOrganizationsResponse,
  ArticlePublication,
  ArticlePublicationsResponse,
  MarkAsPublishedRequest,
  GoogleSearchConsoleIntegration,
  GSCOAuthUrl,
  GSCSitesResponse,
  GSCStatsListResponse,
  GoogleAnalyticsIntegration,
  GAOAuthUrl,
  GAPropertiesResponse,
  GAStatsListResponse,
  WebFlowIntegration,
  WebFlowSitesRequest,
  WebFlowSitesResponse,
  WebFlowCollectionsResponse,
  WebFlowCollectionSchemaResponse,
  WebFlowIntegrationCreateRequest,
  WebFlowFieldMappingUpdateRequest,
} from './types'

const API_BASE = '/api'

/**
 * Custom error class for API errors
 */
export class APIError extends Error {
  constructor(
    message: string,
    public readonly internalCode: number,
    public readonly statusCode: number,
    public readonly response?: APIErrorResponse
  ) {
    super(message)
    this.name = 'APIError'
  }
}

/**
 * Create axios instance with default config
 */
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * Response interceptor to handle errors
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    const axiosError = error as AxiosError<APIErrorResponse>

    if (axiosError.response) {
      // Server responded with error status
      const apiError = axiosError.response.data
      if (apiError && apiError.internal_code !== undefined) {
        const errorMessage = parseAPIError(apiError)
        throw new APIError(
          errorMessage,
          apiError.internal_code,
          apiError.status_code ?? axiosError.response.status,
          apiError
        )
      }
      // Fallback for non-standard error responses
      const errorMessage = 'Unexpected error occurred, please try again later.'
      throw new APIError(
        errorMessage,
        50000,
        axiosError.response.status
      )
    } else if (axiosError.request) {
      // Request was made but no response received (network error)
      throw new APIError(
        'Network error. Please check your connection and try again.',
        50000,
        500
      )
    } else {
      // Something else happened
      throw new APIError(
        parseAPIError(error),
        50000,
        500
      )
    }
  }
)

/**
 * Base API request wrapper
 */
async function apiRequest<T>(config: AxiosRequestConfig): Promise<T> {
  // Don't set Content-Type for FormData - let axios handle it automatically
  // (axios will set multipart/form-data with the correct boundary)
  if (config.data instanceof FormData) {
    config.headers = {
      ...config.headers,
      'Content-Type': undefined,
    }
  }
  const response = await axiosInstance.request<T>(config)
  return response.data
}

/**
 * Auth API endpoints
 */
export const authAPI = {
  /**
   * Sign up a new user
   */
  async signup(email: string, password: string): Promise<{ message: string }> {
    return apiRequest<{ message: string }>({
      url: '/auth/signup',
      method: 'POST',
      data: { email, password },
    })
  },

  /**
   * Sign in an existing user
   */
  async signin(email: string, password: string): Promise<{ message: string }> {
    return apiRequest<{ message: string }>({
      url: '/auth/signin',
      method: 'POST',
      data: { email, password },
    })
  },

  /**
   * Sign out the current user
   */
  async signout(): Promise<{ message: string }> {
    return apiRequest<{ message: string }>({
      url: '/auth/signout',
      method: 'POST',
    })
  },
}

/**
 * User API endpoints
 */
export const userAPI = {
  /**
   * Get current user information with onboarding status
   */
  async getMe(): Promise<UserDetail> {
    return apiRequest<UserDetail>({
      url: '/user/me',
      method: 'GET',
    })
  },

  /**
   * Update user profile
   */
  async updateProfile(data: UserProfileUpdate): Promise<UserDetail> {
    return apiRequest<UserDetail>({
      url: '/user/me',
      method: 'PATCH',
      data,
    })
  },

  /**
   * Complete onboarding (steps 1+2)
   */
  async completeOnboarding(data: OnboardingRequest): Promise<UserDetail> {
    return apiRequest<UserDetail>({
      url: '/user/onboarding',
      method: 'POST',
      data,
    })
  },

  /**
   * Complete onboarding step 3 (create first writer)
   */
  async completeOnboardingWriter(data: OnboardingWriterRequest): Promise<Writer> {
    return apiRequest<Writer>({
      url: '/user/onboarding/writer',
      method: 'POST',
      data,
    })
  },

  /**
   * Get onboarding status
   */
  async getOnboardingStatus(): Promise<OnboardingStatusResponse> {
    return apiRequest<OnboardingStatusResponse>({
      url: '/user/onboarding-status',
      method: 'GET',
    })
  },

  /**
   * Parse business URL for info
   */
  async parseBusinessUrl(url: string): Promise<ParsedBusiness> {
    return apiRequest<ParsedBusiness>({
      url: '/user/parse-business-url',
      method: 'POST',
      data: { url },
    })
  },
}

/**
 * Writers API endpoints
 */
export const writersAPI = {
  /**
   * List user's writers
   */
  async list(): Promise<Writer[]> {
    return apiRequest<Writer[]>({
      url: '/writers',
      method: 'GET',
    })
  },

  /**
   * Get a writer by ID
   */
  async get(writerId: string): Promise<Writer> {
    return apiRequest<Writer>({
      url: `/writers/${writerId}`,
      method: 'GET',
    })
  },

  /**
   * Create a new writer
   */
  async create(data: WriterCreate): Promise<Writer> {
    return apiRequest<Writer>({
      url: '/writers',
      method: 'POST',
      data,
    })
  },

  /**
   * Update a writer
   */
  async update(writerId: string, data: WriterUpdate): Promise<Writer> {
    return apiRequest<Writer>({
      url: `/writers/${writerId}`,
      method: 'PATCH',
      data,
    })
  },

  /**
   * Delete a writer
   */
  async delete(writerId: string): Promise<{ status: string }> {
    return apiRequest<{ status: string }>({
      url: `/writers/${writerId}`,
      method: 'DELETE',
    })
  },

  /**
   * Parse URL for writer config
   */
  async parseUrl(url: string): Promise<ParsedWriter> {
    return apiRequest<ParsedWriter>({
      url: '/writers/parse-url',
      method: 'POST',
      data: { url },
    })
  },
}

/**
 * External Resources API endpoints
 */
export const resourcesAPI = {
  /**
   * List resources by type
   */
  async list(type: ResourceType): Promise<ExternalResource[]> {
    return apiRequest<ExternalResource[]>({
      url: `/resources/${type}`,
      method: 'GET',
    })
  },

  /**
   * Create a resource
   */
  async create(type: ResourceType, data: ExternalResourceCreate): Promise<ExternalResource> {
    return apiRequest<ExternalResource>({
      url: `/resources/${type}`,
      method: 'POST',
      data,
    })
  },

  /**
   * Delete a resource
   */
  async delete(type: ResourceType, resourceId: string): Promise<{ status: string }> {
    return apiRequest<{ status: string }>({
      url: `/resources/${type}/${resourceId}`,
      method: 'DELETE',
    })
  },
}

/**
 * Articles API endpoints
 */
export const articlesAPI = {
  /**
   * List user's articles
   */
  async list(): Promise<ArticleSummary[]> {
    return apiRequest<ArticleSummary[]>({
      url: '/articles',
      method: 'GET',
    })
  },

  /**
   * Get article details
   */
  async get(articleId: string): Promise<ArticleDetail> {
    return apiRequest<ArticleDetail>({
      url: `/articles/${articleId}`,
      method: 'GET',
    })
  },

  /**
   * Create a new article
   */
  async create(data: ArticleCreate): Promise<ArticleDetail> {
    return apiRequest<ArticleDetail>({
      url: '/articles',
      method: 'POST',
      data,
    })
  },

  /**
   * Update article config or plan
   */
  async update(articleId: string, data: ArticleUpdate): Promise<ArticleDetail> {
    return apiRequest<ArticleDetail>({
      url: `/articles/${articleId}`,
      method: 'PATCH',
      data,
    })
  },

  /**
   * Delete an article
   */
  async delete(articleId: string): Promise<{ status: string }> {
    return apiRequest<{ status: string }>({
      url: `/articles/${articleId}`,
      method: 'DELETE',
    })
  },

  /**
   * Generate plan for article
   */
  async generatePlan(articleId: string): Promise<GenerationTaskResponse> {
    return apiRequest<GenerationTaskResponse>({
      url: `/articles/${articleId}/generate-plan`,
      method: 'POST',
    })
  },

  /**
   * Regenerate plan with feedback
   */
  async regeneratePlan(articleId: string, comment?: string): Promise<GenerationTaskResponse> {
    return apiRequest<GenerationTaskResponse>({
      url: `/articles/${articleId}/regenerate-plan`,
      method: 'POST',
      data: { comment },
    })
  },

  /**
   * Approve plan and start content generation
   */
  async approvePlan(articleId: string): Promise<GenerationTaskResponse> {
    return apiRequest<GenerationTaskResponse>({
      url: `/articles/${articleId}/approve-plan`,
      method: 'POST',
    })
  },

  /**
   * Get article generation status
   */
  async getStatus(articleId: string): Promise<ArticleStatus> {
    return apiRequest<ArticleStatus>({
      url: `/articles/${articleId}/status`,
      method: 'GET',
    })
  },

  /**
   * Get article preview with HTML and keyword usage statistics
   */
  async getPreview(articleId: string): Promise<ArticlePreview> {
    return apiRequest<ArticlePreview>({
      url: `/articles/${articleId}/preview`,
      method: 'GET',
    })
  },

  /**
   * Get article preview with merged markdown and keyword usage statistics
   */
  async getPreviewMarkdown(articleId: string): Promise<ArticlePreviewMarkdown> {
    return apiRequest<ArticlePreviewMarkdown>({
      url: `/articles/${articleId}/preview-markdown`,
      method: 'GET',
    })
  },

  /**
   * Unapprove plan - stop content generation and allow plan editing
   */
  async unapprovePlan(articleId: string): Promise<{ status: string }> {
    return apiRequest<{ status: string }>({
      url: `/articles/${articleId}/unapprove-plan`,
      method: 'POST',
    })
  },

  /**
   * Get all schema.org schemas for an article
   */
  async getSchemas(articleId: string): Promise<SchemaOrgSchemaResponse[]> {
    return apiRequest<SchemaOrgSchemaResponse[]>({
      url: `/articles/${articleId}/schemas`,
      method: 'GET',
    })
  },

  /**
   * Download all schema.org schemas for an article as a ZIP file
   */
  async downloadSchemas(articleId: string): Promise<Blob> {
    const response = await axiosInstance.request<Blob>({
      url: `/articles/${articleId}/schemas/download`,
      method: 'GET',
      responseType: 'blob',
    })
    return response.data
  },

  /**
   * Download article content as a ZIP file (markdown + schemas)
   */
  async downloadAll(articleId: string): Promise<Blob> {
    const response = await axiosInstance.request<Blob>({
      url: `/articles/${articleId}/download-all`,
      method: 'GET',
      responseType: 'blob',
    })
    return response.data
  },

  /**
   * Get article planning conversation
   */
  async getPlanningConversation(articleId: string): Promise<ConversationResponse> {
    return apiRequest<ConversationResponse>({
      url: `/articles/${articleId}/planning-conversation`,
      method: 'GET',
    })
  },

  /**
   * Add a message to the planning conversation (user or assistant)
   * If user message, agent will respond and return the agent's message
   */
  async addPlanningMessage(
    articleId: string,
    data: ConversationMessageCreate
  ): Promise<ConversationMessage> {
    return apiRequest<ConversationMessage>({
      url: `/articles/${articleId}/planning-conversation/messages`,
      method: 'POST',
      data,
    })
  },

  /**
   * Add a message to the planning conversation from an audio file
   * The audio is transcribed and processed as a user message with agent response
   */
  async addPlanningMessageFromAudio(
    articleId: string,
    audioFile: File
  ): Promise<ConversationMessage> {
    const formData = new FormData()
    formData.append('audio_file', audioFile)

    const response = await axiosInstance.request<ConversationMessage>({
      url: `/articles/${articleId}/planning-conversation/messages/audio`,
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  /**
   * Summarize conversation and trigger plan generation
   */
  async summarizeAndGeneratePlan(articleId: string): Promise<GenerationTaskResponse> {
    return apiRequest<GenerationTaskResponse>({
      url: `/articles/${articleId}/planning-conversation/summarize-and-generate`,
      method: 'POST',
    })
  },

  /**
   * Clear all conversation messages for an article
   */
  async clearPlanningConversation(articleId: string): Promise<{ status: string }> {
    return apiRequest<{ status: string }>({
      url: `/articles/${articleId}/planning-conversation`,
      method: 'DELETE',
    })
  },

}

/**
 * Plan Blocks API endpoints
 */
export const planBlocksAPI = {
  /**
   * List all plan blocks for an article
   */
  async list(articleId: string): Promise<PlanBlockResponse[]> {
    return apiRequest<PlanBlockResponse[]>({
      url: `/articles/${articleId}/plan-blocks`,
      method: 'GET',
    })
  },

  /**
   * Get a single plan block
   */
  async get(articleId: string, blockId: string): Promise<PlanBlockResponse> {
    return apiRequest<PlanBlockResponse>({
      url: `/articles/${articleId}/plan-blocks/${blockId}`,
      method: 'GET',
    })
  },

  /**
   * Delete a citation, image, or video from a plan block
   */
  async deleteItem(
    articleId: string,
    blockId: string,
    itemType: 'citation' | 'image' | 'video',
    itemIndex: number
  ): Promise<{ status: string }> {
    return apiRequest<{ status: string }>({
      url: `/articles/${articleId}/plan-blocks/${blockId}/items/${itemType}/${itemIndex}`,
      method: 'DELETE',
    })
  },

  /**
   * Add a citation, image, or video to a plan block
   */
  async addItem(
    articleId: string,
    blockId: string,
    itemType: 'citation' | 'image' | 'video',
    data: AddCitationRequest | AddImageRequest | AddVideoRequest
  ): Promise<PlanBlockResponse> {
    return apiRequest<PlanBlockResponse>({
      url: `/articles/${articleId}/plan-blocks/${blockId}/items/${itemType}`,
      method: 'POST',
      data,
    })
  },
}

/**
 * Content Sections API endpoints
 */
export const contentSectionsAPI = {
  /**
   * List all content sections for an article
   */
  async list(articleId: string): Promise<ArticleContentSectionResponse[]> {
    return apiRequest<ArticleContentSectionResponse[]>({
      url: `/articles/${articleId}/content-sections`,
      method: 'GET',
    })
  },

  /**
   * Get a single content section
   */
  async get(articleId: string, sectionId: string): Promise<ArticleContentSectionResponse> {
    return apiRequest<ArticleContentSectionResponse>({
      url: `/articles/${articleId}/content-sections/${sectionId}`,
      method: 'GET',
    })
  },

  /**
   * Update content section markdown manually
   */
  async update(
    articleId: string,
    sectionId: string,
    data: ContentSectionUpdateRequest
  ): Promise<ArticleContentSectionResponse> {
    return apiRequest<ArticleContentSectionResponse>({
      url: `/articles/${articleId}/content-sections/${sectionId}`,
      method: 'PATCH',
      data,
    })
  },

  /**
   * Regenerate content section with AI feedback
   */
  async regenerate(
    articleId: string,
    sectionId: string,
    data: ContentSectionRegenerateRequest
  ): Promise<ArticleContentSectionResponse> {
    return apiRequest<ArticleContentSectionResponse>({
      url: `/articles/${articleId}/content-sections/${sectionId}/regenerate`,
      method: 'POST',
      data,
    })
  },
}

/**
 * Researched Posts API endpoints
 */
export const researchedPostsAPI = {
  /**
   * List researched posts with pagination
   */
  async list(options?: { limit?: number; offset?: number; includeArchived?: boolean }): Promise<ResearchedPostListResponse> {
    const params = new URLSearchParams()
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.offset) params.append('offset', options.offset.toString())
    if (options?.includeArchived) params.append('include_archived', 'true')

    return apiRequest<ResearchedPostListResponse>({
      url: `/researched-posts?${params.toString()}`,
      method: 'GET',
    })
  },

  /**
   * Create a researched post
   */
  async create(data: ResearchedPostCreate): Promise<ResearchedPost> {
    return apiRequest<ResearchedPost>({
      url: '/researched-posts',
      method: 'POST',
      data,
    })
  },

  /**
   * Update a researched post
   */
  async update(postId: string, data: ResearchedPostUpdate): Promise<ResearchedPost> {
    return apiRequest<ResearchedPost>({
      url: `/researched-posts/${postId}`,
      method: 'PATCH',
      data,
    })
  },

  /**
   * Delete a researched post
   */
  async delete(postId: string): Promise<{ status: string }> {
    return apiRequest<{ status: string }>({
      url: `/researched-posts/${postId}`,
      method: 'DELETE',
    })
  },
}

/**
 * Content Ideas API endpoints
 */
export const contentIdeasAPI = {
  /**
   * List content ideas with pagination and filters
   */
  async list(options?: {
    limit?: number
    offset?: number
    includeArchived?: boolean
    plannedDateFrom?: string
    plannedDateTo?: string
  }): Promise<ContentIdeaListResponse> {
    const params = new URLSearchParams()
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.offset) params.append('offset', options.offset.toString())
    if (options?.includeArchived) params.append('include_archived', 'true')
    if (options?.plannedDateFrom) params.append('planned_date_from', options.plannedDateFrom)
    if (options?.plannedDateTo) params.append('planned_date_to', options.plannedDateTo)

    return apiRequest<ContentIdeaListResponse>({
      url: `/content-ideas?${params.toString()}`,
      method: 'GET',
    })
  },

  /**
   * Create a content idea
   */
  async create(data: ContentIdeaCreate): Promise<ContentIdea> {
    return apiRequest<ContentIdea>({
      url: '/content-ideas',
      method: 'POST',
      data,
    })
  },

  /**
   * Update a content idea
   */
  async update(ideaId: string, data: ContentIdeaUpdate): Promise<ContentIdea> {
    return apiRequest<ContentIdea>({
      url: `/content-ideas/${ideaId}`,
      method: 'PATCH',
      data,
    })
  },

  /**
   * Delete a content idea
   */
  async delete(ideaId: string): Promise<{ status: string }> {
    return apiRequest<{ status: string }>({
      url: `/content-ideas/${ideaId}`,
      method: 'DELETE',
    })
  },

  /**
   * Import content ideas from Semrush CSV
   */
  async importSemrush(file: File, params: {
    numIdeas: number
    focusTopics: string
    startDate: string
    minPerWeek: number
    semrushReportUrl?: string
  }): Promise<SemrushImportResponse> {
    const formData = new FormData()
    formData.append('file', file)

    const queryParams = new URLSearchParams()
    queryParams.append('num_ideas', params.numIdeas.toString())
    queryParams.append('focus_topics', params.focusTopics)
    queryParams.append('start_date', params.startDate)
    queryParams.append('min_per_week', params.minPerWeek.toString())
    if (params.semrushReportUrl) {
      queryParams.append('semrush_report_url', params.semrushReportUrl)
    }

    return apiRequest<SemrushImportResponse>({
      url: `/content-ideas/import-semrush?${queryParams.toString()}`,
      method: 'POST',
      data: formData,
    })
  },

  /**
   * Get import job status (for polling)
   */
  async getImportJobStatus(jobId: string): Promise<SemrushImportJobStatus> {
    return apiRequest<SemrushImportJobStatus>({
      url: `/content-ideas/import-jobs/${jobId}`,
      method: 'GET',
    })
  },
}

/**
 * AI API endpoints
 */
export const aiAPI = {
  /**
   * Analyze business URL with AI
   */
  async analyzeBusinessUrl(url: string): Promise<AnalyzedBusiness> {
    return apiRequest<AnalyzedBusiness>({
      url: '/ai/analyze-business-url',
      method: 'POST',
      data: { url },
    })
  },

  /**
   * Analyze writer URL with AI
   */
  async analyzeWriterUrl(url: string): Promise<AnalyzedWriter> {
    return apiRequest<AnalyzedWriter>({
      url: '/ai/analyze-writer-url',
      method: 'POST',
      data: { url },
    })
  },

  /**
   * Analyze writer text with AI
   */
  async analyzeWriterText(text: string): Promise<AnalyzedWriter> {
    return apiRequest<AnalyzedWriter>({
      url: '/ai/analyze-writer-text',
      method: 'POST',
      data: { text },
    })
  },

  /**
   * Trigger AI to research new content topics
   * Returns immediately with task_id (fire-and-forget)
   */
  async researchPosts(data?: ResearchPostsRequest): Promise<GenerationTaskResponse> {
    return apiRequest<GenerationTaskResponse>({
      url: '/ai/research-posts',
      method: 'POST',
      data: data ?? {},
    })
  },
}

/**
 * LinkedIn Integration API endpoints
 */
export const linkedInAPI = {
  /**
   * Get user's LinkedIn integration
   */
  async getIntegration(): Promise<LinkedInIntegration | null> {
    return apiRequest<LinkedInIntegration | null>({
      url: '/linkedin/integration',
      method: 'GET',
    })
  },

  /**
   * Get OAuth authorization URL
   */
  async getOAuthUrl(): Promise<LinkedInOAuthUrl> {
    return apiRequest<LinkedInOAuthUrl>({
      url: '/linkedin/oauth/url',
      method: 'GET',
    })
  },

  /**
   * Get available organizations for a state token
   */
  async getOrganizations(state: string): Promise<LinkedInOrganizationsResponse> {
    return apiRequest<LinkedInOrganizationsResponse>({
      url: `/linkedin/organizations/${state}`,
      method: 'GET',
    })
  },

  /**
   * Select an organization to complete integration
   */
  async selectOrganization(state: string, orgId: string): Promise<LinkedInIntegration> {
    return apiRequest<LinkedInIntegration>({
      url: `/linkedin/organizations/${state}/${orgId}`,
      method: 'POST',
    })
  },

  /**
   * Delete LinkedIn integration
   */
  async deleteIntegration(): Promise<{ status: string }> {
    return apiRequest<{ status: string }>({
      url: '/linkedin/integration',
      method: 'DELETE',
    })
  },
}

/**
 * Google Search Console Integration API endpoints
 */
export const gscAPI = {
  /**
   * Get user's Google Search Console integration
   */
  async getIntegration(): Promise<GoogleSearchConsoleIntegration | null> {
    return apiRequest<GoogleSearchConsoleIntegration | null>({
      url: '/gsc/integration',
      method: 'GET',
    })
  },

  /**
   * Get OAuth authorization URL
   */
  async getOAuthUrl(): Promise<GSCOAuthUrl> {
    return apiRequest<GSCOAuthUrl>({
      url: '/gsc/oauth/url',
      method: 'GET',
    })
  },

  /**
   * Get available sites for a state token
   */
  async getSites(state: string): Promise<GSCSitesResponse> {
    return apiRequest<GSCSitesResponse>({
      url: `/gsc/sites/${state}`,
      method: 'GET',
    })
  },

  /**
   * Select a site to complete integration
   */
  async selectSite(state: string, siteUrl: string): Promise<GoogleSearchConsoleIntegration> {
    return apiRequest<GoogleSearchConsoleIntegration>({
      url: `/gsc/sites/${state}/${encodeURIComponent(siteUrl)}`,
      method: 'POST',
    })
  },

  /**
   * Delete Google Search Console integration
   */
  async deleteIntegration(): Promise<{ status: string }> {
    return apiRequest<{ status: string }>({
      url: '/gsc/integration',
      method: 'DELETE',
    })
  },

  /**
   * Get GSC stats for an article
   */
  async getStats(articleId: string): Promise<GSCStatsListResponse> {
    return apiRequest<GSCStatsListResponse>({
      url: `/gsc/articles/${articleId}/stats`,
      method: 'GET',
    })
  },
}

/**
 * Google Analytics Integration API endpoints
 */
export const gaAPI = {
  /**
   * Get user's Google Analytics integration
   */
  async getIntegration(): Promise<GoogleAnalyticsIntegration | null> {
    return apiRequest<GoogleAnalyticsIntegration | null>({
      url: '/ga/integration',
      method: 'GET',
    })
  },

  /**
   * Get OAuth authorization URL
   */
  async getOAuthUrl(): Promise<GAOAuthUrl> {
    return apiRequest<GAOAuthUrl>({
      url: '/ga/oauth/url',
      method: 'GET',
    })
  },

  /**
   * Get available properties for a state token
   */
  async getProperties(state: string): Promise<GAPropertiesResponse> {
    return apiRequest<GAPropertiesResponse>({
      url: `/ga/properties/${state}`,
      method: 'GET',
    })
  },

  /**
   * Select a property to complete integration
   */
  async selectProperty(state: string, propertyId: string): Promise<GoogleAnalyticsIntegration> {
    return apiRequest<GoogleAnalyticsIntegration>({
      url: `/ga/properties/${state}/${encodeURIComponent(propertyId)}`,
      method: 'POST',
    })
  },

  /**
   * Delete Google Analytics integration
   */
  async deleteIntegration(): Promise<{ status: string }> {
    return apiRequest<{ status: string }>({
      url: '/ga/integration',
      method: 'DELETE',
    })
  },

  /**
   * Get GA stats for an article
   */
  async getStats(articleId: string): Promise<GAStatsListResponse> {
    return apiRequest<GAStatsListResponse>({
      url: `/ga/articles/${articleId}/stats`,
      method: 'GET',
    })
  },
}

/**
 * Publishing API endpoints
 */
export const publishingAPI = {
  /**
   * Get all publication statuses for an article
   */
  async getPublications(articleId: string): Promise<ArticlePublicationsResponse> {
    return apiRequest<ArticlePublicationsResponse>({
      url: `/articles/${articleId}/publish`,
      method: 'GET',
    })
  },

  /**
   * Publish article to LinkedIn
   */
  async publishToLinkedIn(articleId: string): Promise<ArticlePublication> {
    return apiRequest<ArticlePublication>({
      url: `/articles/${articleId}/publish/linkedin`,
      method: 'POST',
    })
  },

  /**
   * Mark article as manually published
   */
  async markAsPublished(articleId: string, data: MarkAsPublishedRequest): Promise<ArticlePublication> {
    return apiRequest<ArticlePublication>({
      url: `/articles/${articleId}/publish/manual`,
      method: 'POST',
      data,
    })
  },
}

/**
 * Webflow Integration API endpoints
 */
export const webflowAPI = {
  /**
   * Get user's Webflow integration
   */
  async getIntegration(): Promise<WebFlowIntegration | null> {
    return apiRequest<WebFlowIntegration | null>({
      url: '/webflow/integration',
      method: 'GET',
    })
  },

  /**
   * List sites for a given Site API Token
   */
  async listSites(data: WebFlowSitesRequest): Promise<WebFlowSitesResponse> {
    return apiRequest<WebFlowSitesResponse>({
      url: '/webflow/sites',
      method: 'POST',
      data,
    })
  },

  /**
   * List collections for a site
   */
  async listCollections(siteId: string, data: WebFlowSitesRequest): Promise<WebFlowCollectionsResponse> {
    return apiRequest<WebFlowCollectionsResponse>({
      url: `/webflow/sites/${siteId}/collections`,
      method: 'POST',
      data,
    })
  },

  /**
   * Get collection schema with fields
   */
  async getCollectionSchema(collectionId: string, data: WebFlowSitesRequest): Promise<WebFlowCollectionSchemaResponse> {
    return apiRequest<WebFlowCollectionSchemaResponse>({
      url: `/webflow/collections/${collectionId}/schema`,
      method: 'POST',
      data,
    })
  },

  /**
   * Create Webflow integration
   */
  async createIntegration(data: WebFlowIntegrationCreateRequest): Promise<WebFlowIntegration> {
    return apiRequest<WebFlowIntegration>({
      url: '/webflow/integration',
      method: 'POST',
      data,
    })
  },

  /**
   * Update field mapping
   */
  async updateFieldMapping(data: WebFlowFieldMappingUpdateRequest): Promise<WebFlowIntegration> {
    return apiRequest<WebFlowIntegration>({
      url: '/webflow/integration/field-mapping',
      method: 'PUT',
      data,
    })
  },

  /**
   * Delete Webflow integration
   */
  async deleteIntegration(): Promise<{ status: string }> {
    return apiRequest<{ status: string }>({
      url: '/webflow/integration',
      method: 'DELETE',
    })
  },
}

// Re-export types for convenience
export * from './types'
